/*
 * =============================================================================
 * FILE HEADER — packages/ir/src/schemas/steps.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why             | What Changed                     |
 * |---|------------------------|-------------------|-----------------|----------------------------------|
 * | 1 | 2026-06-16 (Phase 1)   | Claude Sonnet 4.6 | IR Step 1       | All 24 step types as zod schemas |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - packages/ir/src/schemas/test-ir.ts  (Flow.steps + Hook arrays)
 * - packages/ir/src/index.ts            (re-exported)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * Non-recursive leaf step schemas (evaluated eagerly):
 *   TapStepSchema, DoubleTapStepSchema, LongPressStepSchema,
 *   TypeTextStepSchema, ClearTextStepSchema, PressKeyStepSchema,
 *   HideKeyboardStepSchema, SwipeStepSchema, ScrollUntilVisibleStepSchema,
 *   WaitForStepSchema, WaitStepSchema, AssertStepSchema, ScreenshotStepSchema,
 *   LaunchAppStepSchema, StopAppStepSchema, OpenUrlStepSchema, DeepLinkStepSchema,
 *   SetDeviceStateStepSchema, CallFlowStepSchema, ApiStepSchema, ScriptStepSchema
 *
 * Recursive container step schemas (z.lazy — reference IRStepSchema):
 *   IfStepSchema, RepeatStepSchema, ForEachRowStepSchema
 *
 * IRStepSchema — z.union of all step schemas (z.lazy to break cycle)
 *
 * TypeScript types (inferred or explicitly annotated):
 *   PressKeyName, AssertionKind, TapStep, DoubleTapStep, LongPressStep,
 *   TypeTextStep, ClearTextStep, PressKeyStep, HideKeyboardStep, SwipeStep,
 *   ScrollUntilVisibleStep, WaitForStep, WaitStep, AssertStep, ScreenshotStep,
 *   LaunchAppStep, StopAppStep, OpenUrlStep, DeepLinkStep, SetDeviceStateStep,
 *   IfStep, RepeatStep, ForEachRowStep, CallFlowStep, ApiStep, ScriptStep, IRStep
 *
 * CALLER → CALLEE MAP
 * --------------------
 * test-ir.ts → IRStepSchema
 * index.ts   → all exports
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. IRStepSchema uses z.union (not z.discriminatedUnion) because some of its
 *    members (IfStepSchema, RepeatStepSchema, ForEachRowStepSchema) are z.lazy()
 *    schemas. z.discriminatedUnion eagerly introspects member shapes at construction
 *    time, which breaks for lazy schemas. z.union parses options sequentially (O(n));
 *    acceptable since parsing happens at load/save, not per-step at run time.
 * 2. IRStepSchema itself is z.lazy() because it references IfStepSchema,
 *    RepeatStepSchema, ForEachRowStepSchema which are declared below it. z.lazy()
 *    defers the callback until first use, by which time all consts are initialized.
 * 3. `sensitive: true` on TypeTextStep causes the runner to NEVER log, screenshot,
 *    or record the value. Combine with ${env:KEY} references to keep secrets out of IR.
 * 4. Every step has an optional `options?: Record<string, unknown>` for forward
 *    compatibility. Unknown keys in `options` are preserved on round-trip.
 * 5. `id` fields are ULIDs — time-sortable, stable across file renames.
 *
 * ISSUES FACED
 * ------------
 * - z.discriminatedUnion cannot unwrap z.lazy members → switched to z.union.
 * - Mutually recursive IRStep ↔ IfStep/RepeatStep/ForEachRowStep types require
 *   explicit TypeScript type annotations (Zod cannot infer through the cycle).
 *
 * =============================================================================
 */
import { z } from "zod";
import { SelectorSchema } from "./candidate.js";
// ── PressKey key names ───────────────────────────────────────────────────────
export const PressKeyNameSchema = z.enum(["back", "home", "enter", "tab", "delete"]);
// ── Assertion kinds ──────────────────────────────────────────────────────────
export const AssertionKindSchema = z.enum([
    "visible",
    "notVisible",
    "enabled",
    "disabled",
    "checked",
    "unchecked",
    "hasText",
]);
// ── Common step base (id + options forward-compat bag) ───────────────────────
const StepBaseSchema = z.object({
    id: z.string().min(1),
    options: z.record(z.unknown()).optional(),
});
// ── Leaf step schemas (no recursive reference to IRStepSchema) ───────────────
export const TapStepSchema = StepBaseSchema.extend({
    type: z.literal("tap"),
    selector: SelectorSchema,
    fallbackCoord: z.tuple([z.number(), z.number()]).optional(),
});
export const DoubleTapStepSchema = StepBaseSchema.extend({
    type: z.literal("doubleTap"),
    selector: SelectorSchema,
});
export const LongPressStepSchema = StepBaseSchema.extend({
    type: z.literal("longPress"),
    selector: SelectorSchema,
    durationMs: z.number().positive().default(800),
});
export const TypeTextStepSchema = StepBaseSchema.extend({
    type: z.literal("typeText"),
    text: z.string(),
    sensitive: z.boolean().optional(),
    selector: SelectorSchema.optional(),
});
export const ClearTextStepSchema = StepBaseSchema.extend({
    type: z.literal("clearText"),
    selector: SelectorSchema,
});
export const PressKeyStepSchema = StepBaseSchema.extend({
    type: z.literal("pressKey"),
    key: PressKeyNameSchema,
});
export const HideKeyboardStepSchema = StepBaseSchema.extend({
    type: z.literal("hideKeyboard"),
});
export const SwipeStepSchema = StepBaseSchema.extend({
    type: z.literal("swipe"),
    from: z.tuple([z.number(), z.number()]),
    to: z.tuple([z.number(), z.number()]),
    durationMs: z.number().positive().default(300),
});
export const ScrollUntilVisibleStepSchema = StepBaseSchema.extend({
    type: z.literal("scrollUntilVisible"),
    selector: SelectorSchema,
    direction: z.enum(["up", "down", "left", "right"]).default("down"),
    maxScrolls: z.number().int().positive().default(10),
    scrollable: SelectorSchema.optional(),
});
export const WaitForStepSchema = StepBaseSchema.extend({
    type: z.literal("waitFor"),
    selector: SelectorSchema,
    state: z.enum(["visible", "hidden", "enabled", "disabled"]).default("visible"),
    timeoutMs: z.number().positive().optional(),
});
export const WaitStepSchema = StepBaseSchema.extend({
    type: z.literal("wait"),
    durationMs: z.number().positive(),
});
export const AssertStepSchema = StepBaseSchema.extend({
    type: z.literal("assert"),
    selector: SelectorSchema,
    assertion: AssertionKindSchema,
    value: z.string().optional(),
});
export const ScreenshotStepSchema = StepBaseSchema.extend({
    type: z.literal("screenshot"),
    label: z.string().optional(),
});
export const LaunchAppStepSchema = StepBaseSchema.extend({
    type: z.literal("launchApp"),
    packageName: z.string(),
    clearState: z.boolean().optional(),
});
export const StopAppStepSchema = StepBaseSchema.extend({
    type: z.literal("stopApp"),
    packageName: z.string(),
});
export const OpenUrlStepSchema = StepBaseSchema.extend({
    type: z.literal("openUrl"),
    url: z.string(),
});
export const DeepLinkStepSchema = StepBaseSchema.extend({
    type: z.literal("deepLink"),
    url: z.string(),
    packageName: z.string().optional(),
});
export const SetDeviceStateStepSchema = StepBaseSchema.extend({
    type: z.literal("setDeviceState"),
    wifi: z.boolean().optional(),
    airplane: z.boolean().optional(),
    darkMode: z.boolean().optional(),
    location: z.boolean().optional(),
    showKeyboard: z.boolean().optional(),
});
export const CallFlowStepSchema = StepBaseSchema.extend({
    type: z.literal("callFlow"),
    /** For in-test flow calls (same TestIR). */
    flowId: z.string().optional(),
    /** For Function Library calls (cross-test shared functions). */
    sharedFlowId: z.string().optional(),
    /** Human-readable snapshot of the function name (for export/display). */
    sharedFlowName: z.string().optional(),
    /** Argument bindings: param name → interpolated expression. */
    args: z.record(z.string()).optional(),
});
export const ApiStepSchema = StepBaseSchema.extend({
    type: z.literal("api"),
    method: z.enum(["GET", "POST", "PUT", "PATCH", "DELETE"]),
    url: z.string(),
    headers: z.record(z.string()).optional(),
    body: z.unknown().optional(),
    storeResponseAs: z.string().optional(),
    assertStatus: z.number().int().positive().optional(),
});
export const ScriptStepSchema = StepBaseSchema.extend({
    type: z.literal("script"),
    code: z.string(),
    language: z.enum(["javascript"]).default("javascript"),
});
export const PrintStepSchema = StepBaseSchema.extend({
    type: z.literal("print"),
    message: z.string(),
    level: z.enum(["info", "warn", "error"]).default("info"),
});
// A manual pass/fail verdict placed by the tester. "fail" actively fails the test.
export const CheckpointStepSchema = StepBaseSchema.extend({
    type: z.literal("checkpoint"),
    message: z.string(),
    status: z.enum(["pass", "fail"]),
});
// Immediately stop the current test run with an explicit verdict.
export const StopTestStepSchema = StepBaseSchema.extend({
    type: z.literal("stopTest"),
    message: z.string().default("Test stopped"),
    status: z.enum(["passed", "failed"]).default("failed"),
});
// Control flow signals — only valid inside repeat / forEachRow bodies.
export const BreakStepSchema = StepBaseSchema.extend({ type: z.literal("break") });
export const ContinueStepSchema = StepBaseSchema.extend({ type: z.literal("continue") });
// Capture element text or set a literal value into a named run-time variable.
// Captured value is available as ${name} in all subsequent steps of the same run.
// ── Shared operator + options schemas (also used by IfStep value condition) ───
export const CompareOperatorSchema = z.enum([
    "==", "!=",
    ">", "<", ">=", "<=",
    "contains", "notContains",
    "startsWith", "endsWith",
    "matches", // regex match
    "isEmpty", "isNotEmpty",
]);
export const CompareOptionsSchema = z.object({
    trim: z.boolean().optional(), // trim both sides before compare
    ignoreCase: z.boolean().optional(), // case-insensitive
    parseNumber: z.boolean().optional(), // parse as float for >, <, >=, <=
}).optional();
// ── Evaluate step — compare two interpolated values, store/assert result ──────
export const EvaluateStepSchema = StepBaseSchema.extend({
    type: z.literal("evaluate"),
    /** Left operand — interpolated at runtime (e.g. "${myVar}" or "hello") */
    left: z.string(),
    operator: CompareOperatorSchema,
    /** Right operand — not needed for isEmpty / isNotEmpty */
    right: z.string().optional(),
    options: CompareOptionsSchema,
    /** If set, store "true" or "false" in this variable */
    storeAs: z.string().optional(),
    /** If true, the step status becomes "error" when the comparison is false */
    failIfFalse: z.boolean().optional(),
});
export const SetVariableStepSchema = StepBaseSchema.extend({
    type: z.literal("setVariable"),
    /** Variable name — referenced later as ${name}. Must start with letter/underscore. */
    name: z.string().min(1).regex(/^[a-zA-Z_][a-zA-Z0-9_.]*$/, "Must start with a letter or underscore"),
    source: z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("literal"), value: z.union([z.string(), z.number(), z.boolean()]) }),
        z.object({ kind: z.literal("capture"), selector: SelectorSchema }),
        z.object({
            kind: z.literal("random"),
            randomType: z.enum(["string", "number", "alphanumeric"]),
            length: z.number().int().positive().optional(),
            min: z.number().optional(),
            max: z.number().optional(),
        }),
        z.object({
            kind: z.literal("attribute"),
            selector: SelectorSchema,
            attribute: z.enum([
                "text", "resourceId", "contentDesc", "className", "pkg",
                "enabled", "checked", "clickable", "focused", "selected",
                "scrollable", "longClickable", "password", "checkable",
                "boundsLeft", "boundsTop", "boundsRight", "boundsBottom",
                "boundsWidth", "boundsHeight",
            ]),
        }),
        // Count how many elements match the selector — stored as a string number
        z.object({
            kind: z.literal("count"),
            selector: SelectorSchema,
        }),
    ]),
});
export const HoldSwipeStepSchema = StepBaseSchema.extend({
    type: z.literal("holdSwipe"),
    selector: SelectorSchema,
    direction: z.enum(["up", "down", "left", "right"]).default("down"),
    holdMs: z.number().positive().default(800),
    distance: z.number().positive().default(400),
});
// ── IRStepSchema (lazy union — cycles through IfStep, RepeatStep, ForEachRow) ─
//
// We use `as z.ZodType<IRStep>` instead of a type annotation because .default()
// fields create input/output type divergence that breaks the annotation constraint.
// The cast tells TypeScript "this schema produces IRStep output" without checking
// the input types, which is the standard Zod pattern for recursive union schemas.
export const IRStepSchema = z.lazy(() => z.union([
    TapStepSchema,
    DoubleTapStepSchema,
    LongPressStepSchema,
    TypeTextStepSchema,
    ClearTextStepSchema,
    PressKeyStepSchema,
    HideKeyboardStepSchema,
    SwipeStepSchema,
    ScrollUntilVisibleStepSchema,
    WaitForStepSchema,
    WaitStepSchema,
    AssertStepSchema,
    ScreenshotStepSchema,
    LaunchAppStepSchema,
    StopAppStepSchema,
    OpenUrlStepSchema,
    DeepLinkStepSchema,
    SetDeviceStateStepSchema,
    IfStepSchema,
    RepeatStepSchema,
    ForEachRowStepSchema,
    CallFlowStepSchema,
    ApiStepSchema,
    ScriptStepSchema,
    PrintStepSchema,
    CheckpointStepSchema,
    StopTestStepSchema,
    BreakStepSchema,
    ContinueStepSchema,
    SetVariableStepSchema,
    HoldSwipeStepSchema,
    EvaluateStepSchema,
]));
// ── Recursive container schemas (each lazy — reference IRStepSchema above) ───
export const IfStepSchema = z.lazy(() => StepBaseSchema.extend({
    type: z.literal("if"),
    condition: z.union([
        // LEGACY / element-state condition (kind absent or "element")
        z.object({
            kind: z.literal("element").optional(),
            selector: SelectorSchema,
            assertion: z.enum(["visible", "notVisible", "enabled", "disabled"]),
        }),
        // NEW: value comparison condition
        z.object({
            kind: z.literal("value"),
            left: z.string(),
            operator: CompareOperatorSchema,
            right: z.string().optional(),
            options: CompareOptionsSchema,
        }),
    ]),
    then: z.array(IRStepSchema).min(1),
    else: z.array(IRStepSchema).optional(),
}));
export const RepeatStepSchema = z.lazy(() => StepBaseSchema.extend({
    type: z.literal("repeat"),
    count: z.number().int().positive(),
    steps: z.array(IRStepSchema).min(1),
}));
export const ForEachRowStepSchema = z.lazy(() => StepBaseSchema.extend({
    type: z.literal("forEachRow"),
    dataSource: z.string(),
    variable: z.string(),
    steps: z.array(IRStepSchema).min(1),
}));
//# sourceMappingURL=steps.js.map